import React, { useState } from "react";
import { motion } from "framer-motion";
import { INVEST_FLOW, INVEST_SECTIONS } from "@/lib/perspective";
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip, CartesianGrid } from "recharts";
import { useApp } from "@/context/AppContext";

const SECTION_DATA = {
  microMarket: [{ n: "2022", v: 40 }, { n: "2023", v: 52 }, { n: "2024", v: 61 }, { n: "2025", v: 74 }, { n: "2026", v: 86 }],
  entry: [{ n: "Comp A", v: 68 }, { n: "Comp B", v: 74 }, { n: "This", v: 62 }, { n: "Comp C", v: 80 }],
  demand: [{ n: "Q1", v: 55 }, { n: "Q2", v: 63 }, { n: "Q3", v: 72 }, { n: "Q4", v: 84 }],
  future: [{ n: "Now", v: 60 }, { n: "+2y", v: 72 }, { n: "+4y", v: 83 }, { n: "+6y", v: 91 }],
  exit: [{ n: "Y1", v: 30 }, { n: "Y3", v: 58 }, { n: "Y5", v: 78 }, { n: "Y7", v: 88 }],
};

export const InvestmentPerspective = () => {
  const { theme } = useApp();
  const dark = theme === "dark";
  const [active, setActive] = useState("microMarket");
  const section = INVEST_SECTIONS.find((s) => s.key === active) || INVEST_SECTIONS[0];
  const data = SECTION_DATA[active] || SECTION_DATA.microMarket;

  return (
    <div data-testid="investment-perspective">
      <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
        <span className="font-serif text-2xl text-[var(--accent-gold)]">₹</span>
        {INVEST_FLOW.map((stage, i) => (
          <React.Fragment key={stage}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="rounded-full border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-1.5 text-xs font-medium text-[var(--text-primary)]"
            >
              {stage}
            </motion.div>
            {i < INVEST_FLOW.length - 1 && (
              <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 + 0.1 }} className="hidden h-px w-6 origin-left bg-[var(--accent-a60)] sm:block" />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-12 grid gap-8 lg:grid-cols-[0.85fr_1.15fr]">
        <div className="flex flex-col gap-2">
          {INVEST_SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setActive(s.key)}
              data-testid={`invest-section-${s.key}`}
              className={`rounded-xl border p-4 text-left transition ${active === s.key ? "border-[var(--accent-gold)] bg-[var(--bg-card)]" : "border-[var(--border-subtle)] hover:border-[var(--accent-a50)]"}`}
            >
              <div className="font-serif text-lg text-[var(--text-primary)]">{s.title}</div>
              <div className="mt-1 text-xs text-[var(--text-muted)]">{s.points.slice(0, 3).join(" · ")}</div>
            </button>
          ))}
        </div>

        <motion.div key={active} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="arch-card p-6">
          <div className="overline">{section.title}</div>
          <h3 className="mt-2 font-serif text-2xl font-light text-[var(--text-primary)]">Investment Perspective — {section.title}</h3>
          <div className="mt-5 h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="inv-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--accent-gold)" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="var(--accent-gold)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#2A4F42" : "#E3E4E2"} vertical={false} />
                <XAxis dataKey="n" tick={{ fill: dark ? "#B4C4BA" : "#66756D", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: dark ? "#123629" : "#fff", border: "1px solid var(--accent-gold)", borderRadius: 12, fontSize: 12, color: dark ? "#F5F3ED" : "#0D2B21" }} />
                <Area type="monotone" dataKey="v" stroke="var(--accent-gold)" strokeWidth={2} fill="url(#inv-grad)" animationDuration={1200} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-4 grid grid-cols-2 gap-2">
            {section.points.map((p) => (
              <li key={p} className="flex items-center gap-2 text-sm text-[var(--text-primary)]"><span className="h-1.5 w-1.5 rounded-full bg-[var(--accent-gold)]" />{p}</li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-[var(--text-muted)]">Illustrative Archedia evaluation data. Archedia never guarantees returns or appreciation.</p>
        </motion.div>
      </div>

      <p className="mx-auto mt-12 max-w-2xl text-center font-serif text-2xl font-light leading-snug text-[var(--text-primary)]">
        The property is only one part of the investment. Perspective is what connects the rest.
      </p>
    </div>
  );
};

export default InvestmentPerspective;
